import { clsx } from 'clsx';
import { useRef } from 'react';
import type { KeyboardEvent } from 'react';
import {
  MILES_PROGRAMS,
  type MilesProgramId,
  formatMiles,
} from '../../../utils/payments/mockMiles';

type ProgramSelectorProps = {
  value: MilesProgramId;
  onChange: (next: MilesProgramId) => void;
};

export function ProgramSelector({ value, onChange }: ProgramSelectorProps) {
  const buttonsRef = useRef<(HTMLButtonElement | null)[]>([]);

  function focusIndex(idx: number) {
    const clamped = (idx + MILES_PROGRAMS.length) % MILES_PROGRAMS.length;
    const target = buttonsRef.current[clamped];
    if (target) {
      target.focus();
      onChange(MILES_PROGRAMS[clamped].id);
    }
  }

  function handleKeyDown(idx: number, e: KeyboardEvent<HTMLButtonElement>) {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      focusIndex(idx + 1);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      focusIndex(idx - 1);
    }
  }

  return (
    <div
      role="radiogroup"
      aria-label="Programa de milhas"
      className="grid grid-cols-1 gap-2 sm:grid-cols-3"
    >
      {MILES_PROGRAMS.map((p, i) => {
        const active = value === p.id;
        return (
          <button
            key={p.id}
            ref={(el) => {
              buttonsRef.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(p.id)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            className={clsx(
              'flex flex-col items-start gap-1 rounded-md border px-4 py-3 text-left transition-colors duration-fast',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold',
              active
                ? 'border-gold bg-gold/10 shadow-goldSoft ring-1 ring-gold/30'
                : 'border-gold/20 hover:border-gold/50',
            )}
          >
            <span
              className={clsx(
                'font-display text-base',
                active ? 'text-gold' : 'text-champagne',
              )}
            >
              {p.name}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-stamp text-champagne/70">
              Saldo: {formatMiles(p.balance)} milhas
            </span>
          </button>
        );
      })}
    </div>
  );
}
